import { addMinutes, getDay, getHours, getMinutes, setDay, setHours, setMinutes } from "date-fns";

import { BILLING_TYPES } from "constants";
import generateID from "utils/generateID";
import { convertToLocalTime } from "date-fns-timezone";
/**
 * @typedef {import("api/typesDef").CalendarEvent} CalendarEvent
 * @typedef {import("api/typesDef").AppointmentEvent} AppointmentEvent
 */

const RESIZE_RANGE = 15;

/**
 * Get the weekday of a date.
 * @function getWeekday
 * @param {Date} date - The date to get the weekday for.
 * @returns {number} The weekday (1-7) where 1 is Monday and 7 is Sunday.
 */
function getWeekday(date) {
  const day = getDay(date);
  return day === 0 ? 7 : day;
}

/**
 * Calculate the differences between two calendar events.
 * @function calculateTimeDifferences
 * @param {CalendarEvent} originalEvent - The original event.
 * @param {CalendarEvent} newEvent - The new event.
 * @returns {{hasTimeChange: boolean, hasDayChange: boolean}} The differences between the two events.
 */
function calculateTimeDifferences(originalEvent, newEvent) {
  return {
    hasTimeChange:
      newEvent.start.getHours() !== originalEvent.start.getHours() ||
      newEvent.start.getMinutes() !== originalEvent.start.getMinutes() ||
      newEvent.call_duration !== originalEvent.call_duration,

    hasDayChange: newEvent.start.getDay() !== originalEvent.start.getDay(),
  };
}

/**
 * Removes a day from the repeat days array.
 * @returns {string} The repeat days array with the day removed.
 */
function removeDayFromRepeatDays(repeatDays, dayToRemove) {
  return repeatDays
    .filter((day) => day !== dayToRemove)
    .sort((a, b) => a - b)
    .join(",");
}

/**
 * Replaces a day in the repeat days array.
 * @returns {string} The repeat days array with the day replaced.
 */
function replaceDayInRepeatDays(repeatDays, oldDay, newDay) {
  return repeatDays
    .map((day) => (day === oldDay ? newDay : day))
    .sort((a, b) => a - b)
    .join(",");
}

/**
 * Finds the parent event in the client's schedule.
 * @function findParentEvent
 * @param {Object} clientSchedules - The client's schedule
 * @param {string} parentId - The ID of the parent event
 * @returns {Object|null} The parent event or null if not found
 */
function findParentEvent(clientSchedules, parentId) {
  return clientSchedules.eventsData.find((event) => event.id === parentId);
}

/**
 * Finds the client's schedule in the generated schedules.
 * @function findClientSchedules
 * @param {Array} generatedSchedules - The array of generated schedules
 * @param {string} clientId - The ID of the client
 * @param {string} clinicianId - The ID of the clinician
 * @returns {Object|null} The client's schedule or null if not found
 */
function findClientSchedules(generatedSchedules, clientId, clinicianId) {
  return generatedSchedules
    .find((schedule) => schedule.clinician_id === clinicianId)
    .client_schedules.find((schedule) => schedule.client_id === clientId);
}

/**
 *
 * Sets the date and time for a scheduled event.
 * @param {Object} config - The configuration object for setting the schedule date.
 * @param {number} config.day - The day of the week (1-7) representing Monday to Sunday.
 * @param {?string} config.eventDate - The date of the event in "YYYY-MM-DD HH:MM" format.
 * @param {?string} config.startTime - The time of the event in "HH:MM" format.
 * @param {?string} config.timezone - The timezone of the event.
 * @returns {Date} The scheduled date and time for the event.
 */
function setScheduleDate({ day, eventDate, startTime, timezone }) {
  // If the day is not a number, return the current date
  if (isNaN(day)) return new Date();

  const adjustedDay = day === 7 ? 0 : day; // Adjust Sunday to 0 for setDay function

  // Set the day of the week
  const dayOfWeek = setDay(new Date(), adjustedDay, { weekStartsOn: 0 });
  let hours = 0;
  let minutes = 0;

  // Set the time of the day
  if (eventDate) {
    const initialDate = new Date(eventDate);
    hours = initialDate.getHours();
    minutes = initialDate.getMinutes();
  } else if (startTime) {
    hours = startTime.split(":")[0];
    minutes = startTime.split(":")[1];
  }

  // Set the date and time for the scheduled event
  const scheduledDate = setMinutes(setHours(dayOfWeek, hours), minutes);

  if (timezone) {
    return convertToLocalTime(scheduledDate, {
      timeZone: timezone,
    });
  }

  return scheduledDate;
}

/**
 * Create recurrent events based on the data from the API response.
 * @function createRecurrent
 * @param {Array<AppointmentEvent>} recurringEventData - The array of recurring events data
 * @returns {Array<CalendarEvent>} The array of recurrent events for the calendar
 */
function createRecurrentEvents(recurringEventData = []) {
  const recurringEvents = [];

  recurringEventData.forEach((event) => {
    // Get the days of the week that the event repeats
    const repeatDays = event.repeat_days.split(",").map(Number);

    repeatDays.forEach((day) => {
      if (isNaN(day)) return;

      const scheduledDate = setScheduleDate({
        day,
        startTime: event.start_time,
        timezone: event.call_timezone,
      });

      recurringEvents.push({
        id: event.event_id,
        billing_type: event.billing_type,
        call_duration: event.call_duration,
        call_timezone: event.call_timezone,
        canceled: false,
        client_id: event.client_id,
        client_name: event.client_name,
        clinician_id: event.clinician_id,
        clinician_name: event.clinician_name,
        clinician_image_url: event.clinician_image_url,
        googleEvent: false,
        is_tentative: false,
        no_selectable: true,
        no_show: false,
        start: scheduledDate,
        end: addMinutes(scheduledDate, event.call_duration),
        team_member_name: event.team_member_name,
        title: event?.is_video_call ? BILLING_TYPES[event.billing_type] : event.billing_type,
      });
    });
  });

  return recurringEvents;
}

/**
 * Check if two dates have a time overlap.
 * @function datesHaveTimeOverlap
 * @param {Object} config - The configuration object for checking time overlap.
 * @param {Date} config.date1 - The first date to compare.
 * @param {number} config.date1CallDuration - The call duration for the first date in minutes.
 * @param {Date} config.date2 - The second date to compare.
 * @param {number} config.date2CallDuration - The call duration for the second date in minutes.
 * @returns {boolean} True if the dates have a time overlap, false otherwise.
 */
function datesHaveTimeOverlap({ date1, date1CallDuration, date2, date2CallDuration }) {
  if (!date1 || !date2) return false;
  if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return false;
  // If the dates are not on the same day, return false. Not using specific date comparison
  // because we want to check if the dates are on the same day of the week (for perpetual weekly events)
  if (getDay(date1) !== getDay(date2)) return false;
  // Convert times to minutes since midnight for easier comparison
  const date1StartMinutes = getHours(date1) * 60 + getMinutes(date1);
  const date1EndMinutes = date1StartMinutes + date1CallDuration;
  const date2StartMinutes = getHours(date2) * 60 + getMinutes(date2);
  const date2EndMinutes = date2StartMinutes + date2CallDuration;

  const hasTimeOverlap =
    (date2StartMinutes >= date1StartMinutes && date2StartMinutes < date1EndMinutes) ||
    (date2EndMinutes > date1StartMinutes && date2EndMinutes <= date1EndMinutes) ||
    (date2StartMinutes <= date1StartMinutes && date2EndMinutes >= date1EndMinutes);
  return hasTimeOverlap;
}

/**
 * Create proposed events based on the data from the API response.
 * @function createProposedEvents
 * @param {Array<AppointmentEvent>} dateEvents - The array of recurrent events parents
 * @param {Array<AppointmentEvent>} singleEvents - The array of single events (non recurring events)
 * @returns {Array<CalendarEvent>} The array of proposed events for the calendar
 */
function createProposedEvents(dateEvents = [], singleEvents = []) {
  const proposedEvents = dateEvents.flatMap((event) =>
    event.repeat_days
      ?.split(",")
      .map(Number)
      .map((day) => {
        if (isNaN(day)) return;

        const scheduledDate = setScheduleDate({
          day,
          eventDate: event.scheduled_date,
        });

        // Check for time overlap with single events
        const hasTimeOverlap = singleEvents.some((singleEvent) => {
          const singleEventStartDate = new Date(singleEvent.scheduled_date);

          return datesHaveTimeOverlap({
            date1: scheduledDate,
            date1CallDuration: event.call_duration,
            date2: singleEventStartDate,
            date2CallDuration: singleEvent.call_duration,
          });
        });

        return {
          id: generateID(event.client_id),
          billing_type: event.billing_type,
          conflicted: hasTimeOverlap,
          start: scheduledDate,
          end: addMinutes(scheduledDate, event.call_duration),
          call_duration: event.call_duration,
          call_timezone: event.call_timezone,
          client_id: event.client_id,
          client_name: event.client_name,
          clinician_name: event.clinician_name,
          clinician_id: event.clinician_id,
          clinician_user_id: event.clinician_user_id,
          clinician_image_url: dateEvents?.[0]?.clinician_image_url,
          is_proposed: true,
          is_tentative: true,
          no_selectable: false,
          parent_id: event.id,
          repeat_days: event.repeat_days.split(",").map(Number),
          team_member_name: event.client_name,
          title: BILLING_TYPES[event.billing_type],
        };
      })
  );

  return proposedEvents;
}

/**
 * Updates the generated schedule when an event is modified.
 * @function getUpdatedGeneratedSchedule
 * @param {Object} params - The parameters for updating the generated schedule
 * @param {CalendarEvent} params.originalEvent - The original event before modification
 * @param {CalendarEvent} params.newEvent - The new event after modification
 * @param {Array} params.generatedSchedules - The array of generated schedules
 * @returns {Array} The updated generated schedule
 * @throws {Error} If required parameters are missing
 */
function getUpdatedGeneratedSchedule({ originalEvent, newEvent, generatedSchedules }) {
  if (!originalEvent || !newEvent || !generatedSchedules) {
    throw new Error("getUpdatedGeneratedSchedule: Missing required parameters");
  }

  // Make a copy of the generated schedules
  const newGeneratedSchedule = [...generatedSchedules];

  // Identify the client and clinician from the original event
  const clientId = originalEvent.client_id;
  const clinicianId = originalEvent.clinician_id;

  // Find the client's schedule in the generated schedules
  const clientSchedules = findClientSchedules(newGeneratedSchedule, clientId, clinicianId);

  // Get the difference in days and time between the original and new events
  const differences = calculateTimeDifferences(originalEvent, newEvent);

  // Handle time changes (hour or duration)
  if (differences.hasTimeChange) {
    const oldWeekOfDay = getWeekday(originalEvent.start);
    const newWeekOfDay = getWeekday(newEvent.start);
    // We need to add 1 to the call_duration because when regenetating the event after the resize, the duration is set minus 1
    const newDuration =
      newEvent.call_duration % RESIZE_RANGE !== 0
        ? newEvent.call_duration + 1
        : newEvent.call_duration;

    // Pointing to the original parent event
    const originalAppointment = findParentEvent(clientSchedules, originalEvent.parent_id);

    if (!originalAppointment) {
      throw new Error(`Parent event not found for ID: ${originalEvent.parent_id}`);
    }

    //Set a new scheduled_date based on original scheduled_date and new event start time
    const originalScheduledDate = new Date(originalAppointment.scheduled_date);
    const newDay = getDay(newEvent.start);
    const newDate = setDay(originalScheduledDate, newDay, { weekStartsOn: 0 });

    const newScheduledDate = new Date(
      newDate.setHours(newEvent.start.getHours(), newEvent.start.getMinutes(), 0, 0)
    );

    // Create a new parent event object with the scheduled_date and modified repeat_days
    const newClientSchedulesEvent = {
      ...originalAppointment,
      id: generateID(clientId),
      scheduled_date: newScheduledDate.toISOString(),
      repeat_days: newWeekOfDay.toString(),
      call_duration: newDuration,
    };

    // Remove the day from the repeat_days array of the original parent event
    const repeatDays = removeDayFromRepeatDays(originalEvent.repeat_days, oldWeekOfDay);
    originalAppointment.repeat_days = repeatDays;

    let restOfEvents = [...clientSchedules.eventsData];

    // If the original parent event ran out of repeat_days, remove it from eventsData
    if (repeatDays === "") {
      restOfEvents = clientSchedules.eventsData.filter(
        (event) => event.id !== originalEvent.parent_id
      );
    }

    // Add the new event to the clientSchedules.eventsData
    clientSchedules.eventsData = [...restOfEvents, newClientSchedulesEvent];

    return newGeneratedSchedule;
  }

  // Handle day changes
  if (differences.hasDayChange) {
    const oldWeekOfDay = getWeekday(originalEvent.start);
    const newWeekOfDay = getWeekday(newEvent.start);

    // Replace old day with new day in repeat_days
    const repeatDays = replaceDayInRepeatDays(
      originalEvent.repeat_days,
      oldWeekOfDay,
      newWeekOfDay
    );

    // Find the original parent event
    const originalAppointment = findParentEvent(clientSchedules, originalEvent.parent_id);

    if (!originalAppointment) {
      throw new Error(`Parent event not found for ID: ${originalEvent.parent_id}`);
    }

    // Update the repeat_days of the original parent event
    originalAppointment.repeat_days = repeatDays;

    return newGeneratedSchedule;
  }

  // If no changes are detected, return the original generated schedules
  return generatedSchedules;
}

export {
  createRecurrentEvents,
  createProposedEvents,
  getUpdatedGeneratedSchedule,
  datesHaveTimeOverlap,
  setScheduleDate,
};
